/**
 * Lift2Fit — grouping helpers shared by the lifter's Programme editor and the
 * coach's client builder.
 *
 * A group is a *view* over `ProgrammeRow.supersetGroup` / `groupType`; both
 * screens group, ungroup and reorder through the same
 * `PATCH /programme/:planId/days/:dayId/group` and `/reorder` endpoints, so the
 * only thing worth sharing is how a day's flat rows are read back as clusters.
 * Keeping it here means the coach sees exactly the tags the lifter sees, and a
 * group survives a reorder on both sides by construction.
 */
import type { ExerciseGroupType, ProgrammeRow } from '@/lib/api';

/** One rendered cluster: grouped rows share a number, a solo row has none. */
export interface ExerciseBlock {
  group: number | null;
  groupType: ExerciseGroupType | null;
  rows: ProgrammeRow[];
}

/**
 * Group a day's prescriptions the way the landing page does: rows sharing a
 * group number become one block, in first-encounter order. A row with no group
 * number is its own block. Non-adjacent members still merge, so a group cannot
 * be split by a reorder.
 */
export function buildBlocks(rows: readonly ProgrammeRow[]): ExerciseBlock[] {
  const blocks: ExerciseBlock[] = [];
  const byGroup = new Map<number, ExerciseBlock>();
  for (const row of rows) {
    if (row.supersetGroup === null) {
      blocks.push({ group: null, groupType: null, rows: [row] });
      continue;
    }
    const existing = byGroup.get(row.supersetGroup);
    if (existing) {
      existing.rows.push(row);
      existing.groupType = existing.groupType ?? row.groupType;
    } else {
      const block: ExerciseBlock = { group: row.supersetGroup, groupType: row.groupType, rows: [row] };
      byGroup.set(row.supersetGroup, block);
      blocks.push(block);
    }
  }
  return blocks;
}

/** The next free cluster number for a day, or null when all 12 are taken. */
export function nextGroupNumber(rows: readonly ProgrammeRow[]): number | null {
  const used = new Set(
    rows.map((row) => row.supersetGroup).filter((group): group is number => group !== null),
  );
  for (let candidate = 1; candidate <= 12; candidate += 1) {
    if (!used.has(candidate)) return candidate;
  }
  return null;
}

/**
 * The new day order after moving one prescription `direction` places inside its
 * own cluster. Members of a group move among themselves and ungrouped rows move
 * among the day's ungrouped rows, so a reorder can never split a group. Returns
 * `null` when the move is out of bounds, so the caller sends nothing.
 */
export function moveWithinCluster(
  rows: readonly ProgrammeRow[],
  planExerciseId: string,
  direction: -1 | 1,
): string[] | null {
  const row = rows.find((candidate) => candidate.planExerciseId === planExerciseId);
  if (!row) return null;
  const members = rows.filter((candidate) => candidate.supersetGroup === row.supersetGroup);
  const position = members.findIndex((candidate) => candidate.planExerciseId === planExerciseId);
  const target = position + direction;
  if (position < 0 || target < 0 || target >= members.length) return null;

  const swapped = [...members];
  const a = swapped[position];
  const b = swapped[target];
  if (!a || !b) return null;
  swapped[position] = b;
  swapped[target] = a;

  let cursor = 0;
  return rows.map((candidate) =>
    candidate.supersetGroup === row.supersetGroup
      ? (swapped[cursor++]?.planExerciseId ?? candidate.planExerciseId)
      : candidate.planExerciseId,
  );
}
